import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../web/src/firebaseClient";

import {
  Menu,
  X,
  Rocket,
  Shield,
  CalendarDays,
  LogOut,
  BookOpen
} from "lucide-react";
import logoIcon from "../assets/itk_logo.png";

export default function InnovationHubHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        setUser(currentUser);

        if (!currentUser) {
          setIsAdmin(false);
          setUserRoles({});
          return;
        }

        try {
          const token = await currentUser.getIdTokenResult();

          setIsAdmin(token.claims.role === "admin");

          const userRef = doc(db, "users", currentUser.uid);

          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();

            setUserRoles(userData.roles || {});
            
          } else {
            setUserRoles({});
          }
        } catch (error) {
          console.error("Admin kontrol hatası:", error);

          setIsAdmin(false);
          setUserRoles({});
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(getAuth());
      setUser(null);
      setMobileOpen(false);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Çıkış hatası:", error);
    }
  };


  return (
    <header className="w-full bg-[#eef2f7]">
      <div className="relative w-full overflow-hidden bg-white shadow-md">
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_80%_20%,#1565C0_1px,transparent_1px)] bg-[size:16px_16px]" />

        <div className="relative z-20 flex items-center justify-between px-4 py-4 md:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={logoIcon}
              alt="Logo"
              className="h-[42px] w-[42px] object-contain"
            />
            <div className="leading-tight">
              <div className="text-[15px] font-bold text-[#1565C0] md:text-[16px]">
                Innovation
              </div>
              <div className="text-[15px] font-bold text-[#C62828] md:text-[16px]">
                Hub
              </div>
            </div>
          </Link>

          <nav className="hidden items-center gap-14 lg:flex">
            <NavItem icon={Rocket} label="İTK Starter" to="/itk-starter" />
            <NavItem icon={BookOpen} label="Araştırma Projeleri" to="/research-projects" />
            <NavItem icon={CalendarDays} label="Etkinlikler" to="/events" />
          </nav>

          {/* LOGIN + MENU */}
          <div className="flex items-center gap-3">
            {!user && (
              <Link
                to="/login"
                className="rounded-xl bg-[#1565C0] px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-[#0d47a1]"
              >
                Giriş Yap
              </Link>
            )}

            <button
              onClick={() => setMobileOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow"
            >
              <Menu size={22} color="#1565C0" />
            </button>
          </div>
        </div>

        <div className="h-[3px] w-full bg-gradient-to-r from-[#1565C0] to-[#C62828]" />
      </div>

      {/* overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${
          mobileOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
        onClick={() => setMobileOpen(false)}
      />

      {/* mobile menu */}
      <div
        className={`fixed right-0 top-0 z-50 h-full w-[80%] max-w-[320px] bg-white shadow-xl transition-transform duration-300 ease-in-out ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b px-5 py-4">
          <span className="text-sm font-semibold text-[#173b7a]">Menü</span>

          <button onClick={() => setMobileOpen(false)}>
            <X size={22} />
          </button>
        </div>

        <div className="flex flex-col gap-3 p-4">
          <MobileItem icon={Rocket} label="İTK Starter" to="/itk-starter" onClick={() => setMobileOpen(false)} />
          <MobileItem icon={BookOpen} label="Araştırma Projeleri" to="/research-projects" onClick={() => setMobileOpen(false)} />
          <MobileItem icon={CalendarDays} label="Etkinlikler" to="/events" onClick={() => setMobileOpen(false)} />

          {/* ADMIN */}
          {(isAdmin || userRoles?.supervisor === true) && (
            <MobileItem
              icon={Shield}
              label="Admin Panel"
              to="/admin"
              onClick={() => setMobileOpen(false)}
            />
          )}
          {/* LOGOUT (EN ALT) */}
          {user && (
            <button
              onClick={handleLogout}
              className="mt-4 flex items-center justify-between rounded-xl bg-[#ffecec] px-4 py-3 text-[#C62828]"
            >
              <div className="flex items-center gap-3">
                <LogOut className="h-5 w-5" />
                <span className="text-sm font-medium">Çıkış Yap</span>
              </div>
              <span>›</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function NavItem({ icon: Icon, label, to }) {
  return (
    <Link to={to} className="group flex cursor-pointer items-center gap-2">
      <Icon className="h-5 w-5 text-[#173b7a]" />
      <span className="text-sm font-medium text-[#173b7a] transition group-hover:text-[#C62828]">
        {label}
      </span>
    </Link>
  );
}

function MobileItem({ icon: Icon, label, to, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center justify-between rounded-xl bg-[#f8fbff] px-4 py-3"
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-[#1565C0]" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span>›</span>
    </Link>
  );
}